import React, { Component } from "react";
import axios from "axios";
import { Card, Grid, InputLabel, MenuItem, Select, TextField, Divider } from "@mui/material";
import { Navigate } from "react-router-dom";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

const getEmptyEditor = () => ({
  _id: "",
  question: "",
  option1: "",
  option2: "",
  option3: "",
  option4: "",
  answer: "option1",
  description: "",
  question_type: "easy",
  subject_id: "",
  topic_id: "",
  level_id: "",
  time: 0,
});

export default class ImportMockTestSeriesQuestions extends Component {
  state = {
    loading: false,
    notification: false,
    message: "",
    snackbarColor: "success",
    testseries_id: "",
    testseries: {},
    questionsArray: "",
    existingQuestions: [],
    editorOpen: false,
    editingQuestion: getEmptyEditor(),
    redirect: false,
  };

  componentDidMount() {
    const id = window.location.pathname.split("/")[2];
    this.setState({ testseries_id: id }, () => {
      this.getMockTestSeriesQuestions();
    });
  }

  showMessage = (message, snackbarColor = "success") => {
    this.setState({ notification: true, message, snackbarColor });
    setTimeout(() => {
      this.setState({ notification: false, message: "", snackbarColor: "success" });
    }, 3500);
  };

  getMockTestSeriesQuestions = async () => {
    try {
      if (init() === "success") {
        const resp = await axios.get(
          `${Constants.getUrls.getMockTestSeriesQuestions}${this.state.testseries_id}`
        );
        this.setState({
          testseries: resp?.data?.data?.testseries || {},
          existingQuestions: resp?.data?.data?.questions || [],
          questionsArray: "",
        });
      }
    } catch (err) {
      this.showMessage(
        err?.response?.data?.message || "Failed to load mock test series questions.",
        "warning"
      );
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ loading: true });

    try {
      const payload = {
        testseries_id: this.state.testseries_id,
        questionsArray: this.state.questionsArray,
      };

      const resp = await axios.post(Constants.postUrls.importMockTestSeriesQuestions, payload);
      this.setState({
        loading: false,
        questionsArray: "",
      });
      this.showMessage(resp.data.message);
      this.getMockTestSeriesQuestions();
    } catch (err) {
      this.setState({ loading: false });
      this.showMessage(
        err?.response?.data?.message || "Failed to import mock test series questions.",
        "warning"
      );
    }
  };

  openEditor = (question) => {
    this.setState({
      editorOpen: true,
      editingQuestion: {
        ...getEmptyEditor(),
        ...question,
        subject_id: question?.subject_id || "",
        topic_id: question?.topic_id || "",
        level_id: question?.level_id || "",
      },
    });
  };

  closeEditor = () => {
    this.setState({
      editorOpen: false,
      editingQuestion: getEmptyEditor(),
    });
  };

  handleEditorInput = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      editingQuestion: {
        ...prevState.editingQuestion,
        [name]: value,
      },
    }));
  };

  handleUpdateQuestion = async () => {
    const { editingQuestion } = this.state;

    try {
      const payload = {
        ...editingQuestion,
        subject_id: editingQuestion.subject_id || null,
        topic_id: editingQuestion.topic_id || null,
        level_id: editingQuestion.level_id || null,
      };

      const resp = await axios.put(
        `${Constants.putUrls.updateMockTestSeriesQuestion}${editingQuestion._id}`,
        payload
      );

      this.showMessage(resp.data.message);
      this.closeEditor();
      this.getMockTestSeriesQuestions();
    } catch (err) {
      this.showMessage(
        err?.response?.data?.message || "Failed to update mock test question.",
        "warning"
      );
    }
  };

  handleDeleteQuestion = async (questionId) => {
    try {
      const resp = await axios.delete(
        `${Constants.deleteUrls.deleteMockTestSeriesQuestion}${questionId}`
      );
      this.showMessage(resp.data.message);
      this.getMockTestSeriesQuestions();
    } catch (err) {
      this.showMessage(
        err?.response?.data?.message || "Failed to delete mock test question.",
        "warning"
      );
    }
  };

  renderQuestionCard = (question, index) => {
    const options = ["option1", "option2", "option3", "option4"];

    return (
      <Card key={question._id} sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <MDBox flex={1}>
            <MDTypography variant="button" color="info" fontWeight="bold">
              Question {index + 1}
            </MDTypography>
            <MDTypography variant="h6" mt={1}>
              {question.question}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={() => this.openEditor(question)}
            >
              Edit
            </MDButton>
            <MDButton
              variant="outlined"
              color="error"
              size="small"
              onClick={() => this.handleDeleteQuestion(question._id)}
            >
              Delete
            </MDButton>
          </MDBox>
        </MDBox>

        <Grid container spacing={1.5} mt={0.5}>
          {options.map((optionKey) => (
            <Grid item xs={12} md={6} key={optionKey}>
              <MDBox
                p={1.5}
                borderRadius="lg"
                bgColor={question.answer === optionKey ? "success" : "grey-100"}
                color={question.answer === optionKey ? "white" : "dark"}
              >
                <MDTypography variant="caption" fontWeight="bold" display="block">
                  {optionKey.toUpperCase()}
                </MDTypography>
                <MDTypography variant="button">{question[optionKey]}</MDTypography>
              </MDBox>
            </Grid>
          ))}
        </Grid>

        <MDBox mt={2}>
          <MDTypography variant="caption" fontWeight="bold" color="text">
            Correct Answer: {question.answer}
          </MDTypography>
          <MDTypography variant="caption" display="block" mt={0.75} color="text">
            Type: {question.question_type || "easy"}
          </MDTypography>
          <MDTypography variant="caption" display="block" mt={0.75} color="text">
            Explanation: {question.description || "No explanation added."}
          </MDTypography>
        </MDBox>
      </Card>
    );
  };

  renderEditor = () => {
    const { editingQuestion, editorOpen } = this.state;

    if (!editorOpen) {
      return null;
    }

    return (
      <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <MDTypography variant="h6" mb={2}>
          Edit Mock Test Question
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputLabel>Question</InputLabel>
            <TextField
              multiline
              minRows={3}
              name="question"
              value={editingQuestion.question}
              onChange={this.handleEditorInput}
              style={{ width: "100%" }}
            />
          </Grid>
          {["option1", "option2", "option3", "option4"].map((optionKey) => (
            <Grid item xs={12} md={6} key={optionKey}>
              <InputLabel>{optionKey.toUpperCase()}</InputLabel>
              <TextField
                name={optionKey}
                value={editingQuestion[optionKey]}
                onChange={this.handleEditorInput}
                style={{ width: "100%" }}
              />
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <InputLabel>Correct Answer</InputLabel>
            <Select
              name="answer"
              value={editingQuestion.answer}
              onChange={this.handleEditorInput}
              style={{ width: "100%", height: "45px", color: "#111" }}
            >
              <MenuItem value="option1">Option 1</MenuItem>
              <MenuItem value="option2">Option 2</MenuItem>
              <MenuItem value="option3">Option 3</MenuItem>
              <MenuItem value="option4">Option 4</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <InputLabel>Question Type</InputLabel>
            <Select
              name="question_type"
              value={editingQuestion.question_type}
              onChange={this.handleEditorInput}
              style={{ width: "100%", height: "45px", color: "#111" }}
            >
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="medium-hard">Medium Hard</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
              <MenuItem value="hardest">Hardest</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <InputLabel>Time</InputLabel>
            <TextField
              type="number"
              name="time"
              value={editingQuestion.time}
              onChange={this.handleEditorInput}
              style={{ width: "100%" }}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel>Explanation</InputLabel>
            <TextField
              multiline
              minRows={3}
              name="description"
              value={editingQuestion.description}
              onChange={this.handleEditorInput}
              style={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <MDBox mt={2} display="flex" gap={1}>
          <MDButton
            variant="contained"
            color="info"
            size="small"
            onClick={this.handleUpdateQuestion}
          >
            Save Changes
          </MDButton>
          <MDButton variant="outlined" color="secondary" size="small" onClick={this.closeEditor}>
            Cancel
          </MDButton>
        </MDBox>
      </Card>
    );
  };

  render() {
    if (this.state.redirect) {
      return <Navigate to="/testseries" />;
    }

    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Manage Mock Test Questions
              </MDTypography>
            </MDBox>

            <MDBox py={3} px={2}>
              <MDTypography variant="button" fontWeight="regular" mb={2} display="block">
                {this.state.testseries?.test_series_name || "Mock Test Series"}
                {this.state.existingQuestions.length > 0
                  ? ` • Existing Questions: ${this.state.existingQuestions.length}`
                  : ""}
              </MDTypography>

              {this.renderEditor()}

              <MDTypography variant="h6" mb={2}>
                Existing Questions
              </MDTypography>

              {this.state.existingQuestions.length > 0 ? (
                this.state.existingQuestions.map((question, index) =>
                  this.renderQuestionCard(question, index)
                )
              ) : (
                <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                  <MDTypography variant="button" color="text">
                    No questions added yet for this mock test series.
                  </MDTypography>
                </Card>
              )}

              <Divider sx={{ my: 3 }} />

              <MDTypography variant="h6" mb={2}>
                Add Questions via JSON Array
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={2}>
                Use this section only for bulk import. Existing questions are shown above in
                editable format. For adding a single normal question, use the general add question
                flow.
              </MDTypography>

              <form onSubmit={this.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <InputLabel>Questions JSON Array</InputLabel>
                    <TextField
                      multiline
                      minRows={14}
                      value={this.state.questionsArray}
                      onChange={(e) => this.setState({ questionsArray: e.target.value })}
                      style={{ width: "100%" }}
                      required
                    />
                  </Grid>
                </Grid>

                <Grid align="left" mt={2}>
                  <MDButton variant="contained" size="small" color="info" type="submit">
                    {this.state.loading ? "Importing..." : "Import Questions"}
                  </MDButton>
                </Grid>
              </form>
            </MDBox>

            <MDSnackbar
              color={this.state.snackbarColor}
              icon="notifications"
              title="AgriQuiz"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
